<script lang="ts">
    import { spring } from 'svelte/motion';
    import { fly } from "svelte/transition";
    import { cubicOut } from "svelte/easing";

    import { dismiss, notificationColors, type NotificationMessage } from "./notification";

    export let notification: NotificationMessage;
    const id = notification.id!;

    function slideOut(node: HTMLElement, { delay = 0, duration = 1600 }) {
        const height = node.clientHeight;
        const width = node.clientWidth + node.clientLeft + 2;
        const top = +window.getComputedStyle(node).getPropertyValue("margin-bottom").slice(0, -2);
        const left = +window.getComputedStyle(node).getPropertyValue("margin-left").slice(0, -2);

        const distanceDown = top + height;
        const distanceLeft = left + width;

        return {
            duration,
            easing: cubicOut,
            css: (t: number) => {
                if (t > 0.5) {
                    return `
                    margin-left: calc(${left}px - ${(distanceLeft) * (2 - 2 * t)}px);
                    margin-right: calc(${left}px + ${(distanceLeft) * (2 - 2 * t)}px);
                    opacity: ${2 * t - 1};
                    `;
                } else {
                    return `
                    margin-left: calc(${left}px - ${(distanceLeft)}px);
                    margin-right: calc(${left}px + ${(distanceLeft)}px);
                    margin-bottom: calc(${top}px - ${(distanceDown) * (1 - (t * 2))}px);
                    opacity: 0;
                    `;
                }
            },
        };
    }


    let elem: HTMLElement;
    let position = spring(0, {stiffness: 0.1, damping: 0.3 });
    function onPointerDown(e: PointerEvent) {
        elem.setPointerCapture(e.pointerId);
        e.preventDefault();

        function onPointerMove(moveEvent: PointerEvent) {
            const delta = Math.max(e.clientX - moveEvent.clientX, 0);
            position.set(-delta);
        }

        function onPointerUp(upEvent: PointerEvent) {
            const delta = e.clientX - upEvent.clientX;

            if (delta > elem.offsetWidth / 2) {
                dismiss(id);
            } else {
                position.set(0);
            }

            elem.removeEventListener("pointerup", onPointerUp);
            elem.removeEventListener("pointermove", onPointerMove);
        }

        elem.addEventListener("pointerup", onPointerUp);
        elem.addEventListener("pointermove", onPointerMove);
    }
</script>

<!----------------------------------------------------------------------------->

<div
    bind:this={elem}
    class="notification"
    in:fly={{ x: -10}}
    out:slideOut="{{}}"
    style="--color: {notificationColors[notification.level]}; translate: {$position}px 0;"
     on:pointerdown={onPointerDown}>
    {#if notification.type === "message"}
        <h5>{@html notification.title}</h5>
        <span>{@html notification.message}</span>
    {:else}
        <svelte:component this={notification.component} {...notification.props} />
    {/if}
    <button class="close" on:click={() => dismiss(id)} />
</div>

<!----------------------------------------------------------------------------->

<style>
    .notification {
        background: var(--g100);
        padding: 0.5rem 1rem;
        margin-bottom: 0.5rem;
        margin-inline: 0.5rem;
        outline: 1px solid var(--g400);
        border-left: 4px solid var(--color);
        max-width: 34ch;
        position: relative;
    }

    .close {
        all: unset;
        position: absolute;
        top: 0;
        right: 0;
        width: 1em;
        height: 1em;
        background: red;
    }

    h5 {
        margin: 0;
        font-size: 1em;
        color: var(--t400);
    }

</style>
